import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SharingOptions = ({ tripUrl, onShare }) => {
  const [copied, setCopied] = useState(false);

  const mockTripUrl = tripUrl || "https://parktripplannerai.com/trip/sample-adventure-2025";

  const shareLinks = [
    {
      id: 'twitter',
      name: 'Twitter',
      icon: 'Twitter',
      color: 'hover:bg-blue-50 hover:text-blue-600',
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(mockTripUrl)}&text=Check out my amazing trip itinerary planned with ParkTrip Planner AI!`
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: 'Linkedin',
      color: 'hover:bg-blue-50 hover:text-blue-700',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(mockTripUrl)}`
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: 'MessageCircle',
      color: 'hover:bg-green-50 hover:text-green-600',
      url: `https://wa.me/?text=Check out my trip itinerary: ${encodeURIComponent(mockTripUrl)}`
    }
  ];

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(mockTripUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handlePlatformShare = (platform) => {
    window.open(platform?.url, '_blank', 'width=600,height=400');
    if (onShare) {
      onShare(platform?.id);
    }
  };

  return (
    <div className="bg-card border border-border rounded-organic shadow-organic">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-heading font-heading-bold text-card-foreground">Share Your Trip</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Share your itinerary with friends and family
        </p>
      </div>
      <div className="p-6 space-y-6">
        {/* Copy Link */}
        <div>
          <h4 className="text-sm font-body font-body-semibold text-foreground mb-3">Trip Link</h4>
          <div className="flex items-center space-x-3">
            <div className="flex-1 bg-muted rounded-organic-sm p-3">
              <p className="text-sm text-muted-foreground font-mono truncate">
                {mockTripUrl}
              </p>
            </div>
            <Button
              variant={copied ? "success" : "outline"}
              onClick={handleCopyLink}
              iconName={copied ? "Check" : "Copy"}
              iconPosition="left"
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
        </div>

        {/* Social Media Sharing */}
        <div>
          <h4 className="text-sm font-body font-body-semibold text-foreground mb-3">Share on Social Media</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {shareLinks?.map((platform) => (
              <button
                key={platform?.id}
                onClick={() => handlePlatformShare(platform)}
                className={`flex flex-col items-center space-y-2 p-4 border border-border rounded-organic-sm hover:border-primary/50 transition-organic ${platform?.color} hover:${platform?.color}`}
              >
                <div className={`w-10 h-10 ${platform?.color} rounded-organic flex items-center justify-center`}>
                  <Icon name={platform?.icon} size={20} className={platform?.color} />
                </div>
                <span className="text-sm font-body text-foreground">{platform?.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Email Sharing */}
        <div>
          <h4 className="text-sm font-body font-body-semibold text-foreground mb-3">Email Sharing</h4>
          <div className="flex items-center justify-between p-4 border border-border rounded-organic-sm">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-accent/10 rounded-organic flex items-center justify-center">
                <Icon name="Mail" size={20} className="text-accent" />
              </div>
              <div>
                <p className="font-body font-body-medium text-foreground">Send via Email</p>
                <p className="text-sm text-muted-foreground">Share trip link through email</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                const subject = "Check out my trip itinerary!";
                const body = `I've planned an amazing trip and wanted to share it with you!\n\nView the full itinerary here: ${mockTripUrl}\n\nGenerated with ParkTrip Planner AI`;
                window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
              }}
              iconName="Send"
              iconPosition="left"
            >
              Email Link
            </Button>
          </div>
        </div>

        {/* QR Code Option */}
        <div>
          <h4 className="text-sm font-body font-body-semibold text-foreground mb-3">QR Code</h4>
          <div className="flex items-center justify-between p-4 border border-border rounded-organic-sm">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-organic flex items-center justify-center">
                <Icon name="QrCode" size={20} className="text-primary" />
              </div>
              <div>
                <p className="font-body font-body-medium text-foreground">Generate QR Code</p>
                <p className="text-sm text-muted-foreground">Easy sharing via mobile devices</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                // In a real app, this would generate and show a QR code
                alert('QR Code generation feature coming soon!');
              }}
              iconName="QrCode"
              iconPosition="left"
            >
              Generate QR
            </Button>
          </div>
        </div>

        {/* Sharing Stats */}
        <div className="bg-muted/50 rounded-organic-sm p-4">
          <div className="flex items-start space-x-3">
            <Icon name="TrendingUp" size={16} className="text-primary mt-0.5" />
            <div className="text-sm">
              <p className="font-body font-body-medium text-foreground mb-1">Sharing Benefits</p>
              <ul className="text-muted-foreground space-y-1">
                <li>• Help friends plan similar adventures</li>
                <li>• Get feedback and suggestions</li>
                <li>• Build excitement for your trip</li>
                <li>• Create lasting memories together</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharingOptions;